import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react'

import { BELL_AUDIO_HEALTH_CHECK_INTERVAL_MS } from '@/config/polling'

import { SILENT_PROBE_AUDIO_DATA_URL } from './constants'
import { probeMutedAudioPlayback } from './utils'

export type UserActivityContextValue = {
  isSoundDisabledDueToInactivity: boolean
  tryUnlockBellPlaybackFromUserGesture: () => Promise<void>
}

const UserActivityContext = createContext<UserActivityContextValue | undefined>(
  undefined
)

export type UserActivityProviderProps = PropsWithChildren<{
  healthCheckIntervalMs?: number
}>

export const UserActivityProvider = ({
  children,
  healthCheckIntervalMs = BELL_AUDIO_HEALTH_CHECK_INTERVAL_MS,
}: UserActivityProviderProps) => {
  const [isSoundDisabledDueToInactivity, setIsSoundDisabledDueToInactivity] =
    useState(false)
  const probeAudioRef = useRef<HTMLAudioElement>(null)
  const checkInFlightRef = useRef(false)

  const runPlaybackHealthCheck = useCallback(async () => {
    const audio = probeAudioRef.current
    if (!audio || checkInFlightRef.current) return
    checkInFlightRef.current = true
    try {
      const ok = await probeMutedAudioPlayback(audio)
      setIsSoundDisabledDueToInactivity(!ok)
    } finally {
      checkInFlightRef.current = false
    }
  }, [])

  const tryUnlockBellPlaybackFromUserGesture = useCallback(async () => {
    const audio = probeAudioRef.current
    if (!audio) return
    const ok = await probeMutedAudioPlayback(audio)
    if (ok) {
      setIsSoundDisabledDueToInactivity(false)
    }
  }, [])

  useEffect(() => {
    if (!isSoundDisabledDueToInactivity) return

    const onDocumentClick = () => {
      void tryUnlockBellPlaybackFromUserGesture()
    }

    document.addEventListener('click', onDocumentClick, true)

    return () => {
      document.removeEventListener('click', onDocumentClick, true)
    }
  }, [isSoundDisabledDueToInactivity, tryUnlockBellPlaybackFromUserGesture])

  useEffect(() => {
    void runPlaybackHealthCheck()

    const intervalId = globalThis.setInterval(() => {
      void runPlaybackHealthCheck()
    }, healthCheckIntervalMs)

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void runPlaybackHealthCheck()
      }
    }

    const onWindowFocus = () => {
      void runPlaybackHealthCheck()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    globalThis.addEventListener('focus', onWindowFocus)

    return () => {
      globalThis.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      globalThis.removeEventListener('focus', onWindowFocus)
    }
  }, [healthCheckIntervalMs, runPlaybackHealthCheck])

  const value = useMemo<UserActivityContextValue>(
    () => ({
      isSoundDisabledDueToInactivity,
      tryUnlockBellPlaybackFromUserGesture,
    }),
    [isSoundDisabledDueToInactivity, tryUnlockBellPlaybackFromUserGesture]
  )

  return (
    <UserActivityContext.Provider value={value}>
      <audio
        ref={probeAudioRef}
        src={SILENT_PROBE_AUDIO_DATA_URL}
        preload="auto"
        muted
        tabIndex={-1}
        style={{ display: 'none' }}
      >
        <track kind="captions" />
      </audio>
      {children}
    </UserActivityContext.Provider>
  )
}

export const useUserActivity = (): UserActivityContextValue => {
  const context = useContext(UserActivityContext)
  if (context === undefined) {
    throw new Error(
      'useUserActivity must be used within a UserActivityProvider'
    )
  }
  return context
}
